import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { ButtonWithLoader, Col, Row, Wizard } from '@valid-eval/shared-react-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { success } from '@valid-eval/shared-react-components';
import GenericModal from 'components/GenericModal';
import DNDTable from 'components/Tables/DNDTable';
import { Artifact } from 'data/features/artifactsTypes';
import { useReorderArtifacts, useUpdateArtifact } from 'data/features/eventsHooks';
import useBooleanFlag from 'utils/hooks/useBooleanFlag';
import { useShowErrorIfRejected } from 'utils/hooks/useShowErrorIfRejected';

import { useForm } from 'react-hook-form';
import { EventStepComponentProps } from '.';
import { AddArtifact } from './components/AddArtifact';
import ArtifactForm from './components/ArtifactForm';
import { columns } from './components/ArtifactsTableDefinition';
import EditEventStepForm from './components/EditEventStepForm';

function ArtifactsStepComponent({ event, label, subLabel, nextStep }: EventStepComponentProps) {
  const { t } = useTranslation();
  const { reorderArtifacts, saving } = useReorderArtifacts();
  const { updateArtifactInEvent } = useUpdateArtifact();
  const showErrorIfRejected = useShowErrorIfRejected();
  const [editModalOpen, openEditModal, closeEditModal] = useBooleanFlag(false);
  const [selectedArtifact, setSelectedArtifact] = useState<Artifact | undefined>(undefined);

  const editForm = useForm<Artifact>({
    mode: 'all',
    values: selectedArtifact,
  });

  const handleEdit = (artifact: Artifact) => {
    setSelectedArtifact(artifact);
    openEditModal();
  };

  const handleEditSubmit = async (artifact: Artifact) => {
    if (selectedArtifact) {
      const [, hasError] = showErrorIfRejected(
        await updateArtifactInEvent(event.id, selectedArtifact.id, artifact),
      );
      if (!hasError) {
        success(t('event.form.edit_artifact_success'));
        closeEditModal();
      }
    }
  };

  const tableModel = useReactTable<any>({
    data: event.artifacts || [],
    meta: {
      edit: handleEdit,
    },
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleReorderRow = async (fromIndex: number, toIndex: number) => {
    const [, hasError] = showErrorIfRejected(await reorderArtifacts(event.id, fromIndex, toIndex));
    if (!hasError) success(t('event.form.reorder_artifact_success'));
  };

  return (
    <EditEventStepForm label={label} subLabel={subLabel}>
      <GenericModal
        name="edit-artifact"
        show={editModalOpen}
        onCancel={closeEditModal}
        title={t('event.form.edit_artifact_title')}
        body={<ArtifactForm methods={editForm} event={event} onSubmit={handleEditSubmit} />}
        onConfirm={editForm.handleSubmit(handleEditSubmit)}
        confirmButton={t('common.save')}
        cancelButton={t('common.cancel')}
        isButtonDisabled={saving}
        isCancelButtonDisabled={saving}
      />
      <Row className="mt-3 mb-2">
        <Col xs={12} className="d-flex justify-content-end">
          <AddArtifact event={event} />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <DNDTable tableModel={tableModel} onReorderRow={handleReorderRow} />
        </Col>
      </Row>
      <Wizard.Footer>
        <div className="d-flex justify-content-end align-items-center">
          <ButtonWithLoader variant="primary" loading={saving} onClick={nextStep}>
            {t('event.form.next')}
          </ButtonWithLoader>
        </div>
      </Wizard.Footer>
    </EditEventStepForm>
  );
}

export default ArtifactsStepComponent;
